<template>
  <div v-if="activeActions.length === 0"></div>
  <v-menu v-else v-model="visible" bottom left>
    <template #activator="{ on }">
      <v-btn icon data-qa="show_actions" v-on="on">
        <v-icon>more_vert</v-icon>
      </v-btn>
    </template>

    <!-- Destroy our list and remove it from the DOM, otherwise we will have multiple appear in tests -->
    <v-list v-if="visible" data-qa="grid_actions">
      <v-list-item-group>
        <template v-for="action in activeActions">
          <router-link
            v-if="action.route"
            :key="action.translation(item)"
            :to="getRouteFromAction(action.route)"
            class="router-link"
          >
            <v-list-item>
              <v-list-item-content>
                <v-list-item-title>{{
                  action.translation(item)
                }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </router-link>

          <v-list-item
            v-else
            :key="action.translation(item)"
            @click="action.onClick(item)"
          >
            <v-list-item-content>
              <v-list-item-title>{{
                action.translation(item)
              }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
      </v-list-item-group>
    </v-list>
  </v-menu>
</template>

<script lang="ts">
// Migrated from Class component and has lots of cryptic errors

//@ts-nocheck

import type {
  Renderable,
  RowAction,
} from "@/components/controls/grid/BmGridData";
import { Resize } from "vuetify/lib";
import type { PropType } from "vue";
import { defineComponent } from "vue";

class ScrollWatcher {
  static _allTableWatchers: ScrollWatcher[] = [];
  static _watcherSweeper: number | null = null;

  private scrollCallbacks: ((a: number, b: number) => void)[] = [];
  private readonly handler: (e: Event) => void;
  private headerObserver: IntersectionObserver;
  private intersectionCallbacks: {
    cell: Element;
    callbacks: ((visible: boolean) => void)[];
  }[] = [];
  constructor(protected table: HTMLElement) {
    this.handler = (event: Event) => this.handleTableScroll(event);

    this.headerObserver = new IntersectionObserver(
      (
        entries: IntersectionObserverEntry[],
        observer: IntersectionObserver
      ) => {
        for (let entryObj of entries) {
          const callbacks = this.intersectionCallbacks.find(
            (entry) => entry.cell === entryObj.target
          );
          if (callbacks) {
            callbacks.callbacks.forEach((cb) => cb(entryObj.isIntersecting));
          }
        }
        this.handleTableScroll();
      },
      {
        root: this.table,
      }
    );
  }

  public addScrollCallback(callback: (a: number, b: number) => void) {
    this.scrollCallbacks.push(callback);
    if (this.scrollCallbacks.length == 1) {
      this.table.addEventListener("scroll", this.handler);
    }
  }

  public removeScrollCallback(callback: (a: number, b: number) => void) {
    const idx = this.scrollCallbacks.indexOf(callback);
    if (idx > -1) {
      this.scrollCallbacks = this.scrollCallbacks.splice(idx, 1);
    }
    if (this.scrollCallbacks.length == 0) {
      this.table.removeEventListener("scroll", this.handler);
    }
  }

  public handleTableScroll(event?: Event) {
    // Expensive route: Needs a full CSS Calculation
    const pinnedRightOfGridElementLeftPosition =
      this.table.scrollWidth -
      (this.table.scrollLeft + this.table.getBoundingClientRect().width);

    const scrollBarOffset =
      this.table.scrollHeight > this.table.clientHeight ? 0 : -15;

    this.scrollCallbacks.forEach((fn) =>
      fn(pinnedRightOfGridElementLeftPosition, scrollBarOffset)
    );
  }

  public addHeaderVisibleCallback(
    headerCell: Element,
    callback: (visible: boolean) => void
  ) {
    if (
      !this.intersectionCallbacks.some((entry) => entry.cell === headerCell)
    ) {
      this.intersectionCallbacks.push({ cell: headerCell, callbacks: [] });
      this.headerObserver.observe(headerCell);
    }
    const callbacks = this.intersectionCallbacks.find(
      (entry) => entry.cell === headerCell
    );
    callbacks?.callbacks.push(callback);
  }

  public removeHeaderVisibleCallback(headerCell: Element) {
    const idx = this.intersectionCallbacks.findIndex(
      (cb) => cb.cell === headerCell
    );
    if (idx > -1) {
      this.intersectionCallbacks = this.intersectionCallbacks.splice(idx, 1);
    }
  }

  private cleanup() {
    // Couple of nasty casts here to help GC Along
    this.headerObserver.disconnect();
    this.headerObserver = null as unknown as IntersectionObserver;
    this.intersectionCallbacks = [];
    this.scrollCallbacks = [];
    this.table.removeEventListener("scroll", this.handler);
    this.table = null as unknown as HTMLElement;
  }

  static forTable(table: HTMLElement): ScrollWatcher {
    this._scheduleWatcherCleanup();

    const existing = this._allTableWatchers.find(
      (watcher) => watcher.table === table
    );
    if (existing) {
      return existing;
    }
    const newWatcher = new ScrollWatcher(table);
    this._allTableWatchers.push(newWatcher);
    return newWatcher;
  }

  /* Slightly messy approach..
   * Can't use MutationObserver to be told when elements have been removed from the DOM, because it will only observe
   * child elements being removed: and we don't know what the highest level element being removed is.
   *
   * Would like to use DOMNodeRemoved: but that's been removed from the html spec.
   *
   * So this will sweep through all watchers and check each one to see if the referenced table is still in the DOM:
   * rate limited to at most one run per frame.
   */
  static _scheduleWatcherCleanup() {
    // At most once per frame, clean up old watchers
    if (this._watcherSweeper == null) {
      this._watcherSweeper = window.requestAnimationFrame(() => {
        const toDelete = this._allTableWatchers.filter(
          (watcher) => !document.body.contains(watcher.table)
        );
        this._allTableWatchers = this._allTableWatchers.filter(
          (watcher) => toDelete.indexOf(watcher) < 0
        );

        toDelete.forEach((watcher) => watcher.cleanup());
        this._watcherSweeper = null;
      });
    }
  }
}

export default defineComponent({
  name: "DataCellActions",
  directives: { Resize },
  props: {
    item: {
      default: () => undefined,
    },
    context: {
      type: Object as PropType<Renderable>,
      default: undefined,
      required: false,
    },
  },
  data() {
    return {
      floatMode: false,
      table: null as HTMLElement | null,
      tableCell: null as HTMLElement | null,
      scrollWatcher: null as ScrollWatcher | null,
      visible: false as boolean,
    };
  },
  computed: {
    activeActions() {
      let processedActions: RowAction[] = [];

      for (let action of this.context?.rendererMetadata?.actions ?? []) {
        if (action instanceof Function) {
          processedActions.push(...action(this.item));
        } else {
          processedActions.push(action);
        }
      }
      return processedActions.filter((x: RowAction) => x.isActive(this.item));
    },
  },
  watch: {
    floatMode: {
      // @ts-ignore
      handler: "floatModeWatch",
    },
  },
  mounted() {
    this.table = (this.$el.closest(".bm-grid-table-view") ?? undefined) as
      | HTMLElement
      | undefined;
    this.tableCell = (this.$el.closest("td") ?? undefined) as
      | HTMLElement
      | undefined;

    if (!this.table || !this.tableCell) {
      return;
    }

    // Find the associated header element for this column. This is rather hacky as it uses our td index in the tr to
    // to find the th in the head tr.
    const headerCell =
      this.table.querySelectorAll("thead tr th")[
        [...(this.tableCell.parentNode?.children ?? [])].indexOf(this.tableCell)
      ];
    if (!headerCell) {
      return;
    }

    // listen scroll events to refresh floating action position
    this.scrollWatcher = ScrollWatcher.forTable(this.table);
    this.scrollWatcher.addScrollCallback(this.tableHasScrolled);

    // Set up an observer on the header element to alert us if it becomes visible/hidden.
    this.scrollWatcher.addHeaderVisibleCallback(
      headerCell,
      (visible: boolean) => {
        this.floatMode = !visible;
      }
    );
  },
  beforeDestroy() {
    // cleanup all the changes we may have made.
    this.tableCell?.classList.remove("floating-action", "elevation-3");

    // Try to make garbage collection easier
    this.scrollWatcher?.removeScrollCallback(this.tableHasScrolled);
    this.scrollWatcher?.removeHeaderVisibleCallback(this.tableCell as Element);
    this.scrollWatcher = undefined;
    this.table = undefined;
    this.tableCell = undefined;
  },
  methods: {
    getRouteFromAction(route: ((item: any) => Location) | Location) {
      return route instanceof Function ? route(this.item) : route;
    },
    floatModeWatch() {
      if (this.floatMode) {
        this.tableCell?.classList.add("floating-action", "elevation-3");
      } else {
        this.tableCell?.classList.remove("floating-action", "elevation-3");
      }
    },
    tableHasScrolled(
      pinnedRightOfGridElementLeftPosition: number,
      scrollBarOffset: number
    ) {
      if (!this.floatMode || !this.table || !this.tableCell) {
        return;
      }

      this.tableCell.style.left =
        Math.min(
          0,
          -(pinnedRightOfGridElementLeftPosition + -10 + scrollBarOffset)
        ) + "px";
    },
  },
});
</script>

<style scoped lang="sass">
:deep(button)
  padding: 0

a
  text-decoration: underline
  cursor: pointer

.router-link
  text-decoration: none
</style>

<style lang="sass">
@import "src/sass/colours"

.floating-action
  background: $bm-color-background01 !important // important prevents the hover shade applying in float mode
  position: relative
  width: 10px
</style>
